import { supabase } from '@/integrations/supabase/client';
import { FocusSession, TimerMode } from '@/types/task';

export async function saveFocusSession(session: {
  userId: string;
  taskId: string;
  taskContent: string;
  workType: 'focus' | 'admin';
  timerMode: TimerMode;
  durationMinutes: number;
  startedAt: Date;
  auditNotes?: string;
}): Promise<{ data: FocusSession | null; error: Error | null }> {
  try {
    const { data, error } = await supabase
      .from('focus_sessions')
      .insert({
        user_id: session.userId,
        task_id: session.taskId,
        task_content: session.taskContent,
        work_type: session.workType,
        timer_mode: session.timerMode,
        duration_minutes: session.durationMinutes,
        started_at: session.startedAt.toISOString(),
        audit_notes: session.auditNotes || null,
      })
      .select()
      .single();

    if (error) throw error;
    
    return { data: data as FocusSession, error: null };
  } catch (err) {
    console.error('Error saving focus session:', err);
    return { data: null, error: err as Error };
  }
}

export async function updateFocusSession(
  sessionId: string,
  updates: { duration_minutes: number }
): Promise<{ data: FocusSession | null; error: Error | null }> {
  try {
    const { data, error } = await supabase
      .from('focus_sessions')
      .update({ duration_minutes: updates.duration_minutes })
      .eq('id', sessionId)
      .select()
      .single();

    if (error) throw error;
    return { data: data as FocusSession, error: null };
  } catch (err) {
    console.error('Error updating focus session:', err);
    return { data: null, error: err as Error };
  }
}

/** Fetch focus sessions for a task (e.g. for today) so user can add or edit time */
export async function getFocusSessionsForTask(
  userId: string,
  taskId: string,
  dayStart: Date,
  dayEnd: Date
): Promise<FocusSession[]> {
  const { data, error } = await supabase
    .from('focus_sessions')
    .select('*')
    .eq('user_id', userId)
    .eq('task_id', taskId)
    .gte('started_at', dayStart.toISOString())
    .lte('started_at', dayEnd.toISOString())
    .order('started_at', { ascending: false });

  if (error) {
    console.error('Error fetching focus sessions for task:', error);
    return [];
  }
  return (data as FocusSession[]) || [];
}
